'use client';

import { useMemo } from 'react';
import { toBlob, toJpeg, toSvg } from 'html-to-image';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';

export const IMAGE_BLOB_TYPE = {
  Png: 'png',
  Svg: 'svg',
  Jpeg: 'jpeg',
} as const;
export type ImageBlobType = typeof IMAGE_BLOB_TYPE[keyof typeof IMAGE_BLOB_TYPE];

export interface ElementToBlobVars {
  element: HTMLElement;
  type: ImageBlobType;
  options?: Parameters<typeof toBlob>[1];
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

export async function elementToBlob({ element, type, options }: ElementToBlobVars): Promise<Blob> {
  if (type === IMAGE_BLOB_TYPE.Png) {
    const blob = await toBlob(element, { type: 'image/png', ...options });
    if (!blob) throw new Error('Element image render failed.');
    return blob;
  }
  if (type === IMAGE_BLOB_TYPE.Jpeg) {
    return dataUrlToBlob(await toJpeg(element, options));
  }
  return dataUrlToBlob(await toSvg(element, options));
}

export function useElementToBlob(): ActionResult<ElementToBlobVars, Blob> {
  return useActionHook<ElementToBlobVars, Blob>(
    useMemo(
      () => ({ mutationFn: elementToBlob }),
      [],
    ),
  );
}
